import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, MenuItem, Page, PageContentHeader, PageEmptyState, TextField, Typography } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { useStore, useStoreActions } from "../../contexts";
import { selectors } from "../../store/state-selectors";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import SeeWhatItsLike from "../../components/SeeWhatItsLike";
import BillingHub from "../../components/AgreementMinisite/BillingHub/BillingHub";
import GlobalBillingHubEmptyStateIcon from "./GlobalBillingHubEmptyStateIcon";
import { getClientOptions, getAgreementOptions, getDefaultRelationshipId } from "./GlobalBillingHub.utils";
import img1 from "./img/globalBillingHubPreview1.png";
import img2 from "./img/globalBillingHubPreview2.png";
import styles from "./GlobalBillingHub.module.scss";
const seeWhatItsLikeSteps = [{
  title: 'A single place for managing client billing',
  subtitle: 'Easily view and update upcoming invoices for all of your clients in one place.',
  image: {
    src: img1,
    width: 850,
    height: 497
  }
}, {
  title: 'Keep payments on autopilot, even when making changes',
  subtitle: "Add or remove services, charges, and credits and we'll make sure to automate everything, including client reminders.",
  image: {
    src: img2,
    width: 850,
    height: 505
  }
}];
export function GlobalBillingHubWithData(props) {
  const {
    agreements,
    BH
  } = props;
  const {
    api
  } = useStoreActions();
  const isVendor = useStore(selectors.user.isVendor);
  const {
    urlState: {
      clientId,
      relationshipId
    },
    changeUrlState
  } = useStateFromUrl({
    clientId: {
      defaultValue: ''
    },
    relationshipId: {
      defaultValue: ''
    }
  });
  const [agreementOptions, setAgreementOptions] = useState(() => getAgreementOptions(agreements, clientId));
  const clientOptions = useMemo(() => getClientOptions(agreements), [agreements]);
  const selectedClient = useMemo(() => {
    return clientId ? clientOptions.find(opt => opt.id === clientId) : null;
  }, [clientId, clientOptions]); // Clear selected contact if it doesn't exist in the options.

  useEffect(() => {
    if (clientId && clientOptions.every(o => o.id !== clientId)) {
      changeUrlState({
        clientId: ''
      });
    }
  }, [clientId, clientOptions]); // Clear selected agreement if it doesn't exist in the options.

  useEffect(() => {
    if (relationshipId && agreementOptions.every(o => o.id !== relationshipId)) {
      changeUrlState({
        relationshipId: ''
      });
    }
  }, [relationshipId, agreementOptions]); // Automatically populate relationshipId if it can be populated.

  useEffect(() => {
    if (clientId && !relationshipId && agreementOptions.length) {
      changeUrlState({
        relationshipId: getDefaultRelationshipId(agreementOptions)
      });
    }
  }, [clientId, relationshipId, agreementOptions]);
  return /*#__PURE__*/React.createElement(Page, null, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    className: styles.filters
  }, /*#__PURE__*/React.createElement(Autocomplete, {
    freeSolo: true,
    className: styles.autocomplete,
    "data-testid": "gbh-client",
    options: clientOptions,
    value: selectedClient,
    getOptionLabel: option => option.text,
    textFieldProps: {
      label: 'Client',
      placeholder: 'Choose a client to bill'
    },
    onChange: (_, newOption) => {
      if (!newOption) {
        changeUrlState({
          clientId: '',
          relationshipId: ''
        });
        setAgreementOptions([]);
      } else if (typeof newOption !== 'string' && !Array.isArray(newOption)) {
        const newClientId = newOption.id;
        const newAgreementOptions = getAgreementOptions(agreements, newClientId);
        setAgreementOptions(newAgreementOptions);
        changeUrlState({
          clientId: newClientId,
          relationshipId: getDefaultRelationshipId(newAgreementOptions)
        });
      }
    }
  }), relationshipId && /*#__PURE__*/React.createElement(TextField, {
    select: true,
    width: 400,
    label: "Agreement",
    testid: "gbh-relationship",
    value: relationshipId,
    menuMaxHeight: 400,
    placeholder: "No Agreements to show",
    onChange: e => changeUrlState({
      relationshipId: e.target.value
    })
  }, agreementOptions.map((_ref, idx) => {
    let {
      id,
      text
    } = _ref;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: id,
      value: id,
      "data-testid": "gbh-relationship-opt-".concat(idx)
    }, text);
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.sep
  })), relationshipId ? BH || /*#__PURE__*/React.createElement(RestfulWrapper, {
    key: relationshipId,
    fetch: () => api.relationship.get(relationshipId, {
      resolvePaymentMethod: false
    }),
    renderChild: _ref2 => {
      let {
        data,
        refresh
      } = _ref2;
      return /*#__PURE__*/React.createElement(BillingHub, {
        isVendor: isVendor,
        agreement: data,
        refreshAgreement: refresh
      });
    }
  }) : /*#__PURE__*/React.createElement(Typography, {
    "data-testid": "gbh-no-agreement",
    className: styles.unselected,
    variant: "paragraph2"
  }, "Please choose a client you have an agreement with to start billing"));
}
export default function GlobalBillingHub() {
  const {
    fetchAgreements
  } = useStoreActions();
  useEffect(() => {
    document.title = 'Billing';
  }, []);
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: () => fetchAgreements(),
    getStoreLocation: state => state.agreements,
    renderChild: _ref3 => {
      let {
        data
      } = _ref3;
      const agreements = data;
      return agreements.length ? /*#__PURE__*/React.createElement(GlobalBillingHubWithData, {
        agreements: agreements
      }) : /*#__PURE__*/React.createElement(PageEmptyState, {
        "data-testid": "gbh-empty",
        illustration: /*#__PURE__*/React.createElement(GlobalBillingHubEmptyStateIcon, null),
        title: "Full flexibility with maximum visibility",
        message: "Even fully automated businesses need to get some manual work done. From manual billing to out of agreement charges and credits, with Anchor you have full flexibility.",
        actions: /*#__PURE__*/React.createElement(SeeWhatItsLike, {
          steps: seeWhatItsLikeSteps
        })
      });
    }
  });
}